import React, { useCallback, useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';
import { Helmet } from 'react-helmet';
import { Link, useHistory } from 'react-router-dom';

import {
  Autocomplete,
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import { getRegionCodes } from '@luxuryescapes/lib-regions';

import GridPagination from '~/components/Common/Elements/GridPagination';
import PageSubheader from '~/components/Common/Elements/PageSubheader';

import useCurrentTenant from '~/hooks/useCurrentTenant';
import useQuery from '~/hooks/useQuery';

import {
  GetVoucherDisplayConfigsParams,
  expireVoucherDisplayConfig,
  getVoucherDisplayConfigs,
} from '~/services/PromoService';

import dateFormatter from '~/utils/dateFormatter';

import CopyableField from '../Common/CopyableField';
import DebugModal from '../DebugModal/DebugModal';

// TODO: The contract uses snake_case (PromoDisplayConfig) but this code uses camelCase.
// Using Record<string, any> until the component is migrated to match the contract schema.
type VoucherDisplayConfig = Record<string, any>;
type StatusFilter = 'active' | 'expired' | undefined;

const sharedOpts: Partial<GridColDef> = {
  editable: false,
  sortable: false,
  filterable: false,
  hideable: false,
  disableColumnMenu: true,
  flex: 1,
};

const getColumns = ({ onExpire }: { onExpire: (id: string) => void }): Array<GridColDef<VoucherDisplayConfig>> => [
  {
    field: 'voucherDisplayConfigId',
    headerName: 'General Details',
    ...sharedOpts,
    flex: 0.8,
    renderCell: (params) => (
      <Stack direction="column" spacing={0.5}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography variant="body2">
            <b>ID:</b>
          </Typography>
          <CopyableField
            variant="body2"
            value={params.row.voucherDisplayConfigId}
            label={
              <Link to={`/marketing/voucher-display-config/${params.row.voucherDisplayConfigId}`}>
                {params.row.voucherDisplayConfigId}
              </Link>
            }
          />
        </Stack>
        <Typography variant="body2">
          <b>Region:</b> {params.row.region}
        </Typography>
        <Typography variant="body2">
          <b>Auth:</b> {params.row.authState}
        </Typography>
        <Typography variant="body2">
          <b>Status:</b>{' '}
          <Typography
            component="span"
            variant="body2"
            sx={{
              color: params.row.status === 'active' ? 'success.main' : 'error.main',
              fontWeight: 'bold',
            }}
          >
            {params.row.status}
          </Typography>
        </Typography>
      </Stack>
    ),
  },
  {
    field: 'urlPaths',
    headerName: 'URL Paths',
    ...sharedOpts,
    renderCell: (params) => (
      <Stack direction="column" spacing={0.5}>
        {params.row.urlPaths?.map((path) => (
          <Typography key={path} variant="body2" sx={{ fontWeight: 'bold' }}>
            {path}
          </Typography>
        ))}
      </Stack>
    ),
  },
  {
    field: 'vouchers',
    headerName: 'Vouchers',
    ...sharedOpts,
    display: 'flex',
    renderCell: (params) => (
      <Stack direction="column" spacing={0.5} sx={{ alignSelf: 'flex-start', pt: 1 }}>
        {params.row.vouchers?.map((v) => (
          <Stack key={v.voucherId} direction="row" spacing={0.5} alignItems="flex-start">
            <Typography variant="body2" component={Link} to={`/marketing/vouchers/${v.voucherId}`}>
              {v.voucherId}
            </Typography>
          </Stack>
        ))}
      </Stack>
    ),
  },
  {
    field: 'utm_params',
    headerName: 'UTM Params',
    ...sharedOpts,
    flex: 0.5,
    renderCell: (params) => (
      <Stack direction="column">
        <Typography variant="body2">
          <b>Source:</b> {params.row.source || '-'}
        </Typography>
        <Typography variant="body2">
          <b>Medium:</b> {params.row.medium || '-'}
        </Typography>
        <Typography variant="body2">
          <b>Campaign:</b> {params.row.campaign || '-'}
        </Typography>
      </Stack>
    ),
  },
  {
    field: 'createdAt',
    headerName: 'Created At',
    ...sharedOpts,
    flex: 0.5,
    renderCell: (params) => <Typography variant="body2">{dateFormatter(params.row.createdAt)}</Typography>,
  },
  {
    field: 'actions',
    headerName: 'Actions',
    ...sharedOpts,
    flex: 0.5,
    renderCell: (params) => (
      <Stack direction="column" spacing={1}>
        <Button
          variant="outlined"
          size="small"
          component={Link}
          to={`/marketing/voucher-display-config/${params.row.voucherDisplayConfigId}`}
        >
          Edit
        </Button>
        {params.row.status === 'active' && (
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={() => onExpire(params.row.voucherDisplayConfigId)}
          >
            Expire
          </Button>
        )}
        <DebugModal type="generic" data={params.row} />
      </Stack>
    ),
  },
];

function VoucherDisplayConfigContainer() {
  const query = useQuery();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const { tenant } = useCurrentTenant();

  const [data, setData] = useState<Array<VoucherDisplayConfig>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [pageNum, setPageNum] = useState<number>(query.get('page_num') ? Number(query.get('page_num')) : 0);
  const [pageSize, setPageSize] = useState<number>(query.get('page_size') ? Number(query.get('page_size')) : 10);
  const [region, setRegion] = useState<GetVoucherDisplayConfigsParams['region']>(
    (query.get('region') as GetVoucherDisplayConfigsParams['region']) ?? undefined,
  );
  const [status, setStatus] = useState<StatusFilter | 'all'>((query.get('status') as StatusFilter) || 'active');

  const [expireDialogId, setExpireDialogId] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams();
    if (region) {
      params.set('region', region);
    }
    if (status) {
      params.set('status', status);
    }
    if (pageNum) {
      params.set('page_num', String(pageNum));
    }
    if (pageSize !== 10) {
      params.set('page_size', String(pageSize));
    }
    history.replace({ search: params.toString() });
  }, [region, status, pageNum, pageSize, history]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const {
        result: { voucherDisplayConfigs, total },
      } = await getVoucherDisplayConfigs({
        page: pageNum,
        pageSize,
        status: status === 'all' ? undefined : status,
        brand: tenant.brand as any,
        region: region ?? 'AU',
      });
      setData(voucherDisplayConfigs ?? []);
      setTotalCount(Number(total));
    } catch (error) {
      enqueueSnackbar('Voucher Display Config fetch failed', { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [pageNum, pageSize, status, tenant.brand, region, enqueueSnackbar]);

  useEffect(() => {
    const timer = setTimeout(() => fetchData(), 500);
    return () => clearTimeout(timer);
  }, [fetchData]);

  const handleExpireConfirm = useCallback(async () => {
    if (!expireDialogId) {
      return;
    }
    try {
      await expireVoucherDisplayConfig(expireDialogId);
      enqueueSnackbar('Voucher Display Config expired successfully', { variant: 'success' });
      fetchData();
    } catch (error) {
      enqueueSnackbar(`Failed to expire Voucher Display Config: ${error}`, { variant: 'error' });
    } finally {
      setExpireDialogId(null);
    }
  }, [expireDialogId, enqueueSnackbar, fetchData]);

  const onPageChange = useCallback((page: number, newPageSize: number) => {
    setPageNum(page);
    setPageSize(newPageSize);
  }, []);

  const dataRows = data.map((row, index) => ({
    id: index,
    ...row,
  }));

  return (
    <Container maxWidth="xl" sx={{ width: '90%' }}>
      <Helmet>
        <title>Voucher Display Config</title>
      </Helmet>

      <PageSubheader title={`Search Voucher Display Config [${data.length} of ${totalCount}]`} />

      <Grid container spacing={3}>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 4,
          }}
        >
          <Autocomplete
            id="region"
            options={getRegionCodes()}
            onChange={(_, value) => setRegion((value as any) ?? undefined)}
            renderInput={(params) => <TextField {...params} label="Region" />}
            fullWidth
          />
        </Grid>

        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 4,
          }}
        >
          <FormControl fullWidth variant="outlined">
            <InputLabel id="status-label">Status</InputLabel>
            <Select
              labelId="status-label"
              value={status}
              label="Status"
              onChange={(e) => setStatus(e.target.value as StatusFilter | 'all')}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="expired">Expired</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Box mt={2} display="flex" gap={4}>
            <Button variant="contained" color="primary" component={Link} to="/marketing/voucher-display-config/new">
              New Voucher Display Config
            </Button>
          </Box>
        </Grid>
      </Grid>

      <Box mt={2}>
        <DataGrid
          slots={{ pagination: GridPagination }}
          onPaginationModelChange={({ page, pageSize }) => onPageChange(page, pageSize)}
          loading={isLoading}
          rows={dataRows}
          columns={getColumns({
            onExpire: (id) => setExpireDialogId(id),
          })}
          autoHeight
          rowCount={totalCount}
          paginationMode="server"
          pageSizeOptions={[10, 20, 50]}
          paginationModel={{ page: pageNum, pageSize }}
          rowHeight={150}
        />
      </Box>

      <Dialog open={!!expireDialogId} onClose={() => setExpireDialogId(null)}>
        <DialogTitle>Expire Voucher Display Config</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to expire this voucher display config? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExpireDialogId(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleExpireConfirm}>
            Expire
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default VoucherDisplayConfigContainer;
